/**
 * The export.
 *
 * Aggregates only, suppression already applied, generated from the same
 * derived object the page renders. A cell that is withheld on screen is
 * withheld here, with the same marker, so a reader cannot recover it by
 * exporting. There is no individual-level export and there never will be,
 * because it would defeat the suppression commitment, and the file's own
 * header says so.
 *
 * A module of its own, rather than a function inside the page, so a test can
 * assert what the file carries without rendering React.
 */

import { SUPPRESSION_LEGEND } from "~/app/components/primitives";
import type { CohortView, Distribution } from "~/app/lib/demo-data";
import { asOfStamp } from "~/app/lib/format";
import type { Cell } from "~/lib/suppression";

function csvCell(cell: Cell<number>): [string, string] {
  if (cell.kind === "value") return [String(cell.value), "published"];
  if (cell.kind === "suppressed") return ["", `suppressed: ${cell.reason}`];
  return ["", `not available: ${cell.reason}`];
}

function quote(v: string): string {
  return `"${v.replace(/"/g, '""')}"`;
}

function distributionRows(name: string, distribution: Distribution): string[] {
  const rows = [...distribution.rows];
  if (distribution.remainder) rows.push(distribution.remainder);
  return rows.map((row) => {
    const [value, status] = csvCell(row.cell);
    return [name, row.label, value, status].map(quote).join(",");
  });
}

/** A rate or a ratio, published with the two counts it is built on, as on screen. */
function fractionRow<T extends { readonly numerator: number; readonly denominator: number }>(
  name: string,
  cell: Cell<T>,
  show: (v: T) => string,
  describe: (v: T) => string,
): string {
  return [
    name,
    "",
    cell.kind === "value" ? show(cell.value) : "",
    cell.kind === "value"
      ? `published, ${describe(cell.value)}`
      : cell.kind === "suppressed"
        ? `suppressed: ${cell.reason}`
        : `not available: ${cell.reason}`,
  ]
    .map(quote)
    .join(",");
}

export function buildCsv(view: CohortView): string {
  const { institution, activity, milestones } = view;
  const header = [
    "P3 Enterprise export",
    `Institution,${quote(institution.legalName)}`,
    // A cohort's export says which cohort, so two files cannot be confused (Sep 17 2026).
    `Cohort,${quote(view.cohortLabel ?? "Whole institution")}`,
    `Generated,${quote(asOfStamp(view.asOf))}`,
    `Window,${quote(view.windowLabel)}`,
    `Mentorship filter,${quote(view.filters.mentorship)}`,
    `Comparison,${quote(view.filters.compare)}`,
    "",
    "SUPPRESSION",
    quote(SUPPRESSION_LEGEND),
    quote(
      "Where hiding one figure would let it be recovered by subtracting the others, the next smallest figure is hidden too.",
    ),
    "",
    "WHAT IS NOT IN THIS FILE",
    quote(
      "No individual student rows, ever. Graduation, major, first role and employer appear only as students reported them, in a milestone or their career timeline, and are not verified against a registrar or an employer. P3 holds no GPA, credit, enrollment, salary or credential award record.",
    ),
    "",
    "measure,group,value,status",
  ];

  // The count inside the timeline share, or the share's own withheld state.
  const keepers: Cell<number> =
    view.timelines.withTimeline.kind === "value"
      ? { kind: "value", value: view.timelines.withTimeline.value.numerator }
      : view.timelines.withTimeline;

  const simple: [string, Cell<number>][] = [
    ["students_claimed", view.claimed],
    ["students_with_a_mentor", view.matchedCount],
    ["mentorships_held", view.mentorshipsHeld],
    ["students_waiting_for_a_match", view.waitingTotal],
    ["waiting_with_an_open_request", view.waitingWithRequest],
    ["waiting_who_have_not_asked", view.waitingWithoutRequest],
    ["seats_available", view.seat.available],
    ["milestones_completed", milestones.completed],
    ["students_completing_a_milestone", milestones.students],
    ["students_reporting_an_outcome", milestones.outcomeStudents],
    ["opportunities_opened", activity.opportunitiesOpened.total],
    ["students_who_opened_an_opportunity", activity.opportunitiesOpened.students],
    ["opportunities_applied_to", activity.opportunitiesApplied.total],
    ["students_who_applied", activity.opportunitiesApplied.students],
    ["questions_asked", activity.questions.total],
    ["students_who_asked_a_question", activity.questions.students],
    ["mentor_video_plays", activity.videoPlays.total],
    ["students_who_played_a_mentor_video", activity.videoPlays.students],
    ["videos_saved", activity.videosSaved.total],
    ["students_who_saved_a_video", activity.videosSaved.students],
    ["mentor_follows", activity.mentorsFollowed.total],
    ["students_following_a_mentor", activity.mentorsFollowed.students],
    ["students_not_yet_active", activity.notYetActive],
    ["pulse_ai_sessions", activity.pulse.total],
    ["students_using_pulse_ai", activity.pulse.students],
    ["students_keeping_a_career_timeline", keepers],
    ["career_timeline_entries", view.timelines.entries.total],
    ["students_18_and_over", activity.adults],
    ["pathway_views", activity.pathways.total],
    ["students_exploring_pathways", activity.pathways.students],
    ["students_active_in_app", activity.appUse.active],
    ["questions_asked_in_last_90_days", activity.answers.asked.total],
    ["students_who_asked_in_last_90_days", activity.answers.asked.students],
  ];

  const body = simple.map(([name, cell]) => {
    const [value, status] = csvCell(cell);
    return [name, "", value, status].map(quote).join(",");
  });

  const rate = view.matchedRate;
  body.push(
    [
      "mentorship_reach",
      "",
      rate.kind === "value" ? rate.value.rate.toFixed(4) : "",
      rate.kind === "value"
        ? `published, ${rate.value.numerator} of ${rate.value.denominator}`
        : rate.kind === "suppressed"
          ? `suppressed: ${rate.reason}`
          : `not available: ${rate.reason}`,
    ]
      .map(quote)
      .join(","),
  );

  const { appUse, answers } = activity;
  body.push(
    fractionRow("students_active_in_app_share", appUse.activeShare, (v) => v.rate.toFixed(4), (v) => `${v.numerator} of ${v.denominator}`),
    fractionRow(
      "sessions_per_active_student",
      appUse.sessionsPerStudent,
      (v) => v.ratio.toFixed(2),
      (v) => `${v.numerator} sessions over ${v.denominator} students`,
    ),
    fractionRow(
      "average_session_seconds",
      appUse.averageSessionSeconds,
      (v) => String(Math.round(v.ratio)),
      (v) => `${v.numerator} seconds over ${v.denominator} sessions`,
    ),
    fractionRow("questions_answered_share", answers.answered, (v) => v.rate.toFixed(4), (v) => `${v.numerator} of ${v.denominator} questions`),
    /* The status column carries the denominator, which is the whole point of
       this row: "8 of 14 students who could be compared" cannot be misread as
       a share of the institution the way a bare 0.5714 could. */
    fractionRow(
      "first_role_in_own_field_share",
      milestones.fieldMatch,
      (v) => v.rate.toFixed(4),
      (v) => `${v.numerator} of ${v.denominator} students who could be compared`,
    ),
  );

  const weekly = (name: string, points: readonly { readonly weekEnding: string; readonly cumulative: Cell<number> }[]) =>
    points.map((point) => {
      const [value, status] = csvCell(point.cumulative);
      return [name, point.weekEnding, value, status].map(quote).join(",");
    });

  return [
    ...header,
    ...body,
    ...weekly("cumulative_students_by_week", view.growth.points),
    ...weekly("cumulative_milestones_by_week", milestones.byWeek.points),
    ...weekly("cumulative_questions_by_week", answers.weekly),
    ...distributionRows("how_students_joined", view.channels),
    ...distributionRows("support_area", view.supportAreas),
    ...distributionRows("industry_interest", view.industries),
    ...distributionRows("education_goal", view.educationGoals),
    ...distributionRows("meeting_rhythm", view.meetingRhythms),
    ...distributionRows("mentorships_per_student", view.mentorshipCounts),
    ...distributionRows("top_milestones", milestones.top),
    ...distributionRows("outcomes_reported", milestones.outcomes),
    ...distributionRows("pathways_explored", activity.pathwaysExplored),
    // Entries, not students, and the names say so (Sep 12 2026).
    ...distributionRows("career_timeline_entries_by_stage", view.timelines.byStage),
    ...distributionRows("career_timeline_entries_by_kind", view.timelines.byKind),
  ].join("\n");
}

/** "-college-of-nursing" for a cohort's file, nothing for the institution's. */
export function cohortSlug(label: string | undefined): string {
  if (!label) return "";
  const slug = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40);
  return slug ? `-${slug}` : "";
}

export function downloadCsv(view: CohortView): void {
  const blob = new Blob([buildCsv(view)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `p3-${view.institution.joinSlug}${cohortSlug(view.cohortLabel)}-${view.asOf.slice(0, 10)}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
